import { useMemo, useState } from 'react';
import { useStrings } from '@/i18n/Localization';
import { DEFAULT_REACTIONS, MAX_REACTIONS_PER_USER } from '@/protocol/limits';
import type { ReactionRecord } from '@/store/types';
import styles from '@/components/panels.module.css';

export interface ReactionBarProps {
  reactions: readonly ReactionRecord[];
  ownUserId: string;
  /** Section 06: a protected conversation still permits reactions. */
  disabled: boolean;
  onToggle: (reaction: string, active: boolean) => void;
}

/**
 * Reaction counts and the picker for the six defaults.
 *
 * Section 06 fixes both halves: the six default reactions, and at most three
 * distinct reactions per user per message. The limit is enforced here as well
 * as in the store, because a control the user can press that always fails is
 * worse than one that explains why it is unavailable.
 *
 * Counts are derived from the stored rows rather than from a projected count
 * field. Section 06 keeps exact source rows and projects counts asynchronously,
 * so a locally derived count is the one that agrees with "did I react?" at the
 * moment the user presses the button.
 */
export function ReactionBar(props: ReactionBarProps) {
  const { t } = useStrings();
  const [picking, setPicking] = useState(false);

  const { counts, mine } = useMemo(() => {
    const tally = new Map<string, number>();
    const own = new Set<string>();
    for (const row of props.reactions) {
      if (!row.active) {
        continue;
      }
      tally.set(row.reaction, (tally.get(row.reaction) ?? 0) + 1);
      if (row.userId === props.ownUserId) {
        own.add(row.reaction);
      }
    }
    return { counts: tally, mine: own };
  }, [props.reactions, props.ownUserId]);

  const atLimit = mine.size >= MAX_REACTIONS_PER_USER;
  const ordered = DEFAULT_REACTIONS.filter((emoji) => (counts.get(emoji) ?? 0) > 0);

  return (
    <div className={styles.reactions}>
      {ordered.map((emoji) => {
        const count = counts.get(emoji) ?? 0;
        const isMine = mine.has(emoji);
        return (
          <button
            key={emoji}
            type="button"
            className={`${styles.reaction} ${isMine ? styles.reactionMine : ''}`}
            // The accessible name states whether this is the viewer's own
            // reaction; the border alone would be information conveyed by
            // appearance, which Section 14 does not permit.
            aria-label={
              isMine
                ? t('reaction.remove', { emoji })
                : t('reaction.count', { emoji, count })
            }
            aria-pressed={isMine}
            disabled={props.disabled || (!isMine && atLimit)}
            onClick={() => {
              props.onToggle(emoji, !isMine);
            }}
          >
            <span aria-hidden="true">{emoji}</span>
            <span aria-hidden="true">{count}</span>
          </button>
        );
      })}

      {props.disabled ? null : (
        <button
          type="button"
          className={styles.reaction}
          aria-haspopup="menu"
          aria-expanded={picking}
          aria-label={t('reaction.add')}
          onClick={() => {
            setPicking((open) => !open);
          }}
        >
          <span aria-hidden="true">+</span>
        </button>
      )}

      {picking ? (
        <div className={styles.reactionPicker} role="menu" aria-label={t('reaction.picker')}>
          {DEFAULT_REACTIONS.map((emoji) => {
            const isMine = mine.has(emoji);
            return (
              <button
                key={emoji}
                type="button"
                role="menuitem"
                className={styles.reactionChoice}
                aria-label={isMine ? t('reaction.remove', { emoji }) : t('reaction.apply', { emoji })}
                disabled={!isMine && atLimit}
                onClick={() => {
                  setPicking(false);
                  props.onToggle(emoji, !isMine);
                }}
              >
                <span aria-hidden="true">{emoji}</span>
              </button>
            );
          })}
          {atLimit ? <p className={styles.hint}>{t('reaction.limit')}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
